// src/components/BMWArtCarTracker/index.jsx
import React, { useState, useEffect, useMemo } from 'react';
import 'leaflet/dist/leaflet.css';
import Header from './Header';
import Timeline from './Timeline';
import MapView from './MapView';
import CarDetails from './CarDetails';
import CarSpotlight from './CarDetails/CarSpotlight';
import ModalOverlay from './ModalOverlay';
import { events } from '../../utils/data/events';

/**
 * Main BMW Art Car Tracker component
 */
const BMWArtCarTracker = () => {
  // States for UI
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(2); // March (0-indexed)
  const [currentYear, setCurrentYear] = useState(2025);
  const [mapCenter] = useState([0, 30]);
  const [mapZoom] = useState(1.5);
  const [spotlightCar, setSpotlightCar] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Handle opening car spotlight
  const handleViewCarDetails = (car) => {
    setSpotlightCar(car);
    setIsModalOpen(true);
  };

  // Handle closing car spotlight
  const handleCloseSpotlight = () => {
    setIsModalOpen(false);
    // Optional: keep the car data in state and only clear when modal animation completes
    setTimeout(() => setSpotlightCar(null), 300);
  };

  // Simulate active events (today's date in the app)
  // Using useMemo to prevent recreation on every render
  const today = useMemo(() => {
    const date = new Date();
    date.setFullYear(2025);
    date.setMonth(2);
    date.setDate(15);
    return date;
  }, []);

  useEffect(() => {
    // Update which events are currently active
    const updatedEvents = events.map(event => {
      const active = today >= event.startDate && today <= event.endDate;
      return { ...event, active };
    });
    
    // Find the first upcoming event if none is selected
    if (!selectedEvent) {
      const upcoming = updatedEvents.find(event => event.startDate >= today);
      if (upcoming) {
        setSelectedEvent(upcoming);
      } else {
        setSelectedEvent(updatedEvents[0]);
      }
    }
  }, [selectedEvent, today]);

  const goToPreviousMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const goToNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  const filteredEvents = events.filter(event => {
    const eventStartMonth = event.startDate.getMonth();
    const eventStartYear = event.startDate.getFullYear();
    const eventEndMonth = event.endDate.getMonth();
    const eventEndYear = event.endDate.getFullYear();
    
    // Check if event is in the current month/year or spans across it
    return (
      (eventStartYear === currentYear && eventStartMonth === currentMonth) ||
      (eventEndYear === currentYear && eventEndMonth === currentMonth) ||
      (eventStartYear === currentYear && eventStartMonth < currentMonth && 
       eventEndYear === currentYear && eventEndMonth > currentMonth) ||
      (eventStartYear < currentYear && 
       eventEndYear > currentYear) ||
      (eventStartYear < currentYear && 
       eventEndYear === currentYear && eventEndMonth >= currentMonth) ||
      (eventStartYear === currentYear && eventStartMonth <= currentMonth && 
       eventEndYear > currentYear)
    );
  }).sort((a, b) => a.startDate - b.startDate);

  return (
    <div className="flex flex-col h-screen max-h-screen bg-gray-50">
      <Header />
      
      <main className="flex-1 overflow-hidden flex">
        {/* Left side - Timeline */}
        <div className="w-1/3 h-full">
          <Timeline 
            events={events}
            filteredEvents={filteredEvents}
            selectedEvent={selectedEvent}
            onSelectEvent={setSelectedEvent}
            currentMonth={currentMonth}
            currentYear={currentYear}
            onPreviousMonth={goToPreviousMonth}
            onNextMonth={goToNextMonth}
          />
        </div>
        
        {/* Middle - Map */}
        <div className="w-1/3">
          <MapView 
            events={events}
            selectedEvent={selectedEvent}
            mapCenter={mapCenter}
            mapZoom={mapZoom}
            onEventSelect={setSelectedEvent}
          />
        </div>
        
        {/* Right - Event details */}
        <div className="w-1/3">
          <CarDetails 
            selectedEvent={selectedEvent}
            onViewCarDetails={handleViewCarDetails}
          />
        </div>
      </main>

      {/* Modal for car spotlight */}
      <ModalOverlay 
        isOpen={isModalOpen} 
        onClose={handleCloseSpotlight}
      >
        <CarSpotlight 
          car={spotlightCar} 
          onClose={handleCloseSpotlight} 
        />
      </ModalOverlay>
    </div>
  );
};

export default BMWArtCarTracker;

